import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { ClaimState } from "../../store/VnvAssistStore";
import { useVnvAssistStore } from "../../store/VnvAssistStore";
import { useDeleteVnvAssistClaim } from "../../services/VnvAssistServices";
import { NucToast } from "src/reusable/components/Toast";

type ClaimsTabProps = {
  claims: ClaimState[];
};

const ClaimsTab = ({ claims }: ClaimsTabProps) => {
  const {
    activeClaim,
    setActiveClaim,
    validationStatus,
    vnvRunId,
    removeClaim,
  } = useVnvAssistStore();

  const isIdentifying = validationStatus === "identifying";
  const canDelete = validationStatus === "identified";
  const { mutate: deleteClaim } = useDeleteVnvAssistClaim();

  // Per-claim "delete in flight" set. A claim's row shows a spinner and the
  // delete button is disabled until the backend confirms.
  const [pendingDeletions, setPendingDeletions] = useState<Set<number>>(
    () => new Set(),
  );

  const handleDeleteClaim = (e: React.MouseEvent, claimId: number) => {
    e.stopPropagation();
    if (!vnvRunId) return;
    if (pendingDeletions.has(claimId)) return; // already in flight

    setPendingDeletions((prev) => {
      const next = new Set(prev);
      next.add(claimId);
      return next;
    });

    const clearPending = () =>
      setPendingDeletions((prev) => {
        if (!prev.has(claimId)) return prev;
        const next = new Set(prev);
        next.delete(claimId);
        return next;
      });

    deleteClaim(
      { runId: vnvRunId, claimId },
      {
        onSuccess: () => {
          removeClaim(claimId);
          clearPending();
        },
        onError: () => {
          clearPending();
          NucToast({
            message: "Failed to delete claim. Please try again.",
            type: "error",
          });
        },
      },
    );
  };

  if (claims.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ p: 3, opacity: 0.5 }}
      >
        <Typography variant="body2" textAlign="center">
          {isIdentifying
            ? "Identifying claims..."
            : "Claim identification starting..."}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Claim</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "12%" }}>Page</TableCell>
              {canDelete && <TableCell sx={{ width: "6%", p: 0 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {claims.map((claim) => {
              const isActive = activeClaim?.id === claim.id;
              return (
                <TableRow
                  key={claim.id}
                  hover
                  selected={isActive}
                  sx={{ cursor: "pointer" }}
                  onClick={() => setActiveClaim(claim)}
                >
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {claim.text}
                      </Typography>
                      {claim.source_citation && (
                        <Chip
                          label={claim.source_citation}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: "0.6rem",
                            height: 16,
                            alignSelf: "flex-start",
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {claim.page_number ?? "—"}
                    </Typography>
                  </TableCell>

                  {canDelete && (
                    <TableCell sx={{ p: 0 }}>
                      {(() => {
                        const isDeleting = pendingDeletions.has(claim.id);
                        return (
                          <Tooltip
                            title={isDeleting ? "Deleting…" : "Remove claim"}
                            placement="left"
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={isDeleting}
                                onClick={(e) => handleDeleteClaim(e, claim.id)}
                                sx={{
                                  opacity: isDeleting ? 1 : 0.5,
                                  "&:hover": { opacity: 1 },
                                }}
                              >
                                {isDeleting ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <DeleteOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ClaimsTab;
